import type { FavoriteShortcut, Project, SmartList } from '@portfolio-pm/shared';
import { Settings } from 'lucide-react';
import { useMemo } from 'react';
import { ScrollViewport } from '../../layout/ScrollViewport.tsx';
import type { TaskWorkspaceView, buildTaskWorkspaceCounts } from '../task-workspace-model.ts';
import { SYSTEM_VIEWS } from './options.ts';

/** Slide-in list/saved-view/project selector for the mobile Tasks workspace. */
export function MobileTaskSelector({
  open,
  view,
  projects,
  smartLists,
  favoriteShortcuts,
  counts,
  onSelect,
  onOpenSettings,
}: {
  open: boolean;
  view: TaskWorkspaceView;
  projects: Project[];
  smartLists: SmartList[];
  favoriteShortcuts: FavoriteShortcut[];
  counts: ReturnType<typeof buildTaskWorkspaceCounts>;
  onSelect(view: TaskWorkspaceView): void;
  onOpenSettings?(): void;
}): JSX.Element {
  const favoriteSmartListIds = useMemo(
    () =>
      new Set(
        favoriteShortcuts
          .filter((shortcut) => shortcut.kind === 'smart')
          .map((shortcut) => shortcut.id),
      ),
    [favoriteShortcuts],
  );
  const favoriteItems = useMemo(
    () =>
      favoriteShortcuts
        .map((shortcut) => {
          if (shortcut.kind === 'project') {
            const project = projects.find((candidate) => candidate.id === shortcut.id);
            return project
              ? { shortcut, name: project.name, view: projectView(project.id) }
              : undefined;
          }
          const smartList = smartLists.find((candidate) => candidate.id === shortcut.id);
          return smartList
            ? { shortcut, name: smartList.name, view: smartListView(smartList.id) }
            : undefined;
        })
        .filter((item): item is MobileFavoriteItem => item !== undefined),
    [favoriteShortcuts, projects, smartLists],
  );
  const visibleSmartLists = useMemo(
    () => smartLists.filter((smartList) => !favoriteSmartListIds.has(smartList.id)),
    [favoriteSmartListIds, smartLists],
  );

  return (
    <aside
      data-testid="mobile-task-selector"
      data-open={open || undefined}
      aria-hidden={!open}
      className={`mobile-standalone-safe-top absolute inset-y-0 left-0 z-10 hidden w-[min(82vw,20rem)] flex-col border-r border-[color:var(--app-border)] bg-[color:var(--app-mobile-panel-bg)] px-3 pb-4 shadow-sm transition-transform duration-200 ease-out max-md:flex ${
        open ? 'translate-x-0' : '-translate-x-full'
      }`}
    >
      <p className="eyebrow-label">Task Views</p>
      <ScrollViewport className="mt-3 min-h-0 flex-1" viewportClassName="h-full pr-1">
        <MobileSelectorSection title="System Views">
          {SYSTEM_VIEWS.map((systemView) => (
            <MobileSelectorButton
              key={systemView.id}
              active={view.kind === 'system' && view.id === systemView.id}
              count={counts.system[systemView.id]}
              testId={`mobile-sidebar-system-${systemView.id}`}
              onClick={() => onSelect({ kind: 'system', id: systemView.id })}
            >
              {systemView.label}
            </MobileSelectorButton>
          ))}
        </MobileSelectorSection>

        <MobileSelectorSection title="Favorites">
          {favoriteItems.length === 0 ? (
            <p className="surface-empty p-3 text-xs">No favorites yet.</p>
          ) : (
            favoriteItems.map((item) => (
              <MobileSelectorButton
                key={`${item.shortcut.kind}:${item.shortcut.id}`}
                active={isSameTaskView(view, item.view)}
                count={
                  item.view.kind === 'project'
                    ? (counts.projects.get(item.view.projectId) ?? 0)
                    : (counts.smartLists.get(item.view.smartListId) ?? 0)
                }
                testId={`mobile-sidebar-favorite-${item.shortcut.kind}-${item.shortcut.id}`}
                onClick={() => onSelect(item.view)}
              >
                {item.name}
              </MobileSelectorButton>
            ))
          )}
        </MobileSelectorSection>

        <MobileSelectorSection title="Saved Views">
          {visibleSmartLists.length === 0 ? (
            <p className="surface-empty p-3 text-xs">No saved views yet.</p>
          ) : (
            visibleSmartLists.map((smartList) => (
              <MobileSelectorButton
                key={smartList.id}
                active={view.kind === 'smart' && view.smartListId === smartList.id}
                count={counts.smartLists.get(smartList.id) ?? 0}
                testId={`mobile-sidebar-smart-${smartList.id}`}
                onClick={() => onSelect({ kind: 'smart', smartListId: smartList.id })}
              >
                {smartList.name}
              </MobileSelectorButton>
            ))
          )}
        </MobileSelectorSection>

        <MobileSelectorSection title="Projects">
          {projects.map((project) => (
            <MobileSelectorButton
              key={project.id}
              active={view.kind === 'project' && view.projectId === project.id}
              count={counts.projects.get(project.id) ?? 0}
              testId={`mobile-sidebar-project-${project.id}`}
              onClick={() => onSelect({ kind: 'project', projectId: project.id })}
            >
              {project.name}
            </MobileSelectorButton>
          ))}
        </MobileSelectorSection>

        {onOpenSettings && (
          <section className="mt-6 border-t border-[color:var(--app-border)] pb-2 pt-5">
            <p className="eyebrow-label mb-2">Settings</p>
            <button
              type="button"
              data-testid="mobile-sidebar-settings"
              onClick={onOpenSettings}
              className="ui-button flex w-full items-center justify-start gap-2 px-3 py-2 text-sm"
            >
              <Settings aria-hidden="true" size={16} />
              Open settings
            </button>
          </section>
        )}
      </ScrollViewport>
    </aside>
  );
}

type MobileFavoriteItem = {
  shortcut: FavoriteShortcut;
  name: string;
  view: MobileFavoriteView;
};

type MobileFavoriteView =
  | Extract<TaskWorkspaceView, { kind: 'project' }>
  | Extract<TaskWorkspaceView, { kind: 'smart' }>;

function projectView(projectId: string): MobileFavoriteView {
  return { kind: 'project', projectId };
}

function smartListView(smartListId: string): MobileFavoriteView {
  return { kind: 'smart', smartListId };
}

function isSameTaskView(a: TaskWorkspaceView, b: TaskWorkspaceView): boolean {
  if (a.kind !== b.kind) return false;
  if (a.kind === 'project' && b.kind === 'project') return a.projectId === b.projectId;
  if (a.kind === 'smart' && b.kind === 'smart') return a.smartListId === b.smartListId;
  if (a.kind === 'system' && b.kind === 'system') return a.id === b.id;
  if (a.kind === 'tag' && b.kind === 'tag') return a.tag === b.tag;
  return false;
}

function MobileSelectorSection({
  title,
  children,
}: {
  title: string;
  children: JSX.Element | JSX.Element[];
}): JSX.Element {
  return (
    <section className="mt-5 first:mt-0">
      <p className="eyebrow-label mb-2">{title}</p>
      <div className="space-y-1">{children}</div>
    </section>
  );
}

function MobileSelectorButton({
  active,
  count,
  testId,
  onClick,
  children,
}: {
  active: boolean;
  count: number;
  testId: string;
  onClick(): void;
  children: string;
}): JSX.Element {
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={onClick}
      className={`flex w-full items-center justify-between rounded-xl px-3 py-2 text-left text-sm transition ${
        active
          ? 'border border-[color:var(--app-border-strong)] bg-[color:var(--app-accent-soft)] text-[color:var(--app-accent-text)]'
          : 'font-medium text-[color:var(--app-text)] hover:bg-[color:var(--app-control-hover)]'
      }`}
    >
      <span className="truncate">{children}</span>
      <span className="ui-chip px-2 py-0.5 text-[11px]">{count}</span>
    </button>
  );
}
